/**
 * @fileoverview Styled components for Teacher Signup
 * @description Two-column layout optimized for desktop and mobile
 */

import styled from 'styled-components'
import {
  ARC_BORDER,
  ARC_CARD_RADIUS_PX,
  ARC_CARD_SHADOW,
  ARC_PURPLE,
  ARC_PURPLE_HOVER,
  cardEnter as ARC_CARD_ENTER,
} from 'components/ArcAuthPage'

/** Main page container */
export const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
`

/** Logo container */
export const LogoContainer = styled.div`
  padding: 1.5rem 2rem;
  flex-shrink: 0;
`

/** Two-column layout */
export const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  padding: 0 2rem;
  gap: 40px;

  @media (min-width: 1300px) {
    max-width: 1300px;
    margin: 0 auto;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 1rem;
    gap: 2rem;
  }
`

/** Left column - illustration */
export const IllustrationColumn = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding-top: 1rem;

  @media (max-width: 900px) {
    display: none;
  }
`

/** Illustration image */
export const IllustrationImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  object-fit: contain;
`

/** Right column - form */
export const FormColumn = styled.div`
  flex: 0 0 auto;
  width: 420px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 1rem 0 2rem 0;

  @media (min-width: 1100px) {
    width: 505px;
  }

  @media (max-width: 900px) {
    width: 100%;
    max-width: 505px;
    margin: 0 auto;
    padding: 1rem;
  }
`

/** Form wrapper */
export const Form = styled.form`
  width: 100%;
`

/** Name fields row */
export const NameFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`

/** Terms text */
export const TermsText = styled.p`
  font-size: 0.8125rem;
  color: #868e96;
  text-align: center;
  margin-top: 1.25rem;
  line-height: 1.6;

  a {
    color: #7c3aed;
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: #6d28d9;
      text-decoration: underline;
    }
  }
`

/** Toggle buttons for school selection */
export const ToggleContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

/* ——— Arc variants below (AR-792). Only rendered when the arc flag is on;
   the legacy components above stay byte-identical for the flag-off path. ——— */

/** Two-column grid mirroring the site hero (disc left, form right) */
export const ArcContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 505px;
  gap: 80px;
  align-items: center;
  max-width: 1180px;
  margin: 0 auto;
  padding: 24px 28px 32px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 24px 16px 48px;
  }
`

/** Disc illustration column (hidden on mobile, like the site hero) */
export const ArcIllustrationColumn = styled.div`
  width: 100%;
  max-width: 600px;

  @media (max-width: 900px) {
    display: none;
  }
`

/** White form card — same values as the site .contact-form (copy, don't reuse) */
export const ArcFormCard = styled.div`
  background-color: #fff;
  border: 1.5px solid ${ARC_BORDER};
  border-radius: ${ARC_CARD_RADIUS_PX}px;
  box-shadow: ${ARC_CARD_SHADOW};
  padding: 28px 36px;
  animation: ${ARC_CARD_ENTER} 0.25s ease-out;

  a {
    color: ${ARC_PURPLE};

    &:hover {
      color: ${ARC_PURPLE_HOVER};
    }
  }

  @media (max-width: 900px) {
    max-width: 505px;
    margin: 0 auto;
    width: 100%;
  }

  @media (max-width: 500px) {
    padding: 28px 24px;
  }
`
